import type { QuestionDraft } from './questionTypes'
import { answerModeFromQuestionType, surveyKindFromQuestionType } from './questionTypes'
import {
  buildScaleColsFromScaleType,
  decodeSurveyMeta,
  pickScaleTypeFromQuestion,
  scaleMaxFromScaleType
} from 'shared/api/managerQuestionnaireQuestion'
import { resolveFilesBurgdevUrl } from 'shared/utils/resolveFilesBurgdevUrl'

export type ExplainModeHydrated =
  | 'never'
  | 'after_submit'
  | 'if_passed'
  | 'after_each'

type LangCode = 'ru' | 'en'

type Bilingual = Record<LangCode, string>

type LocalizedName = { lang: string; name: string }

function langKey(p: Record<string, unknown>): string {
  const raw =
    p.lang ?? p.Lang ?? p.language ?? p.Language ?? p.locale ?? p.Locale
  return typeof raw === 'string' ? raw.trim().toLowerCase() : ''
}

function textFromPart(p: Record<string, unknown>): string {
  const v =
    p.name ?? p.Name ?? p.value ?? p.Value ?? p.text ?? p.Text ?? p.title ?? p.Title
  return typeof v === 'string' ? v : ''
}

export function pickLocalizedText(parts: unknown, preferredLang: string): string {
  if (!Array.isArray(parts) || parts.length === 0) {
    return ''
  }
  const arr = parts as LocalizedName[]
  const lang = preferredLang.trim().toLowerCase().slice(0, 2)
  const rows = arr.map((p) =>
    p && typeof p === 'object' ? (p as unknown as Record<string, unknown>) : {}
  )
  const pref = rows.find((p) => langKey(p) === lang && textFromPart(p))
  const prefText = pref ? textFromPart(pref) : ''
  if (prefText) {
    return prefText
  }
  return pickName(arr, 'ru')
}

function pickName(parts: LocalizedName[] | undefined, lang: LangCode): string {
  if (!Array.isArray(parts) || parts.length === 0) {
    return ''
  }
  const rows = parts.map((p) =>
    p && typeof p === 'object' ? (p as unknown as Record<string, unknown>) : {}
  )
  const pref = rows.find((p) => langKey(p) === lang && textFromPart(p))
  const prefText = pref ? textFromPart(pref) : ''
  if (prefText) {
    return prefText
  }
  const ru = rows.find((p) => langKey(p) === 'ru')
  const ruText = ru ? textFromPart(ru) : ''
  if (ruText) {
    return ruText
  }
  const en = rows.find((p) => langKey(p) === 'en')
  const enText = en ? textFromPart(en) : ''
  if (enText) {
    return enText
  }
  const first = rows.find((p) => textFromPart(p))
  return first ? textFromPart(first) : ''
}

export function mapLocalizedToBilingual(parts: unknown): Bilingual {
  if (!Array.isArray(parts)) {
    return { ru: '', en: '' }
  }
  const arr = parts as LocalizedName[]
  return {
    ru: pickName(arr, 'ru'),
    en: pickName(arr, 'en')
  }
}

export function timeToCompleteToMinutes(timeStr: unknown): string {
  const s = String(timeStr ?? '').trim()
  if (!s) {
    return '0'
  }
  let days = 0
  let timePart = s
  const dotIdx = s.indexOf('.')
  if (dotIdx !== -1) {
    const d = parseInt(s.slice(0, dotIdx), 10)
    if (Number.isNaN(d)) {
      return '0'
    }
    days = d
    timePart = s.slice(dotIdx + 1)
  }
  const parts = timePart.split(':').map((x) => parseInt(x, 10))
  if (parts.some((n) => Number.isNaN(n))) {
    return '0'
  }
  const h = parts[0] ?? 0
  const m = parts[1] ?? 0
  const sec = parts[2] ?? 0
  return String(days * 24 * 60 + h * 60 + m + Math.floor(sec / 60))
}

export function mapExplainTypeToMode(n: unknown): ExplainModeHydrated {
  const v = Number(n)
  switch (v) {
    case 0:
      return 'never'
    case 1:
      return 'after_submit'
    case 2:
      return 'if_passed'
    case 3:
      return 'after_each'
    default:
      return 'after_submit'
  }
}

function pickOptionText(text: unknown): string {
  if (typeof text === 'string') {
    return text
  }
  if (text !== null && typeof text === 'object' && !Array.isArray(text)) {
    return textFromPart(text as Record<string, unknown>)
  }
  if (!Array.isArray(text)) {
    return ''
  }
  const arr = text as LocalizedName[]
  return pickName(arr, 'ru') || pickName(arr, 'en') || ''
}

function pickQuestionText(text: unknown): string {
  return pickOptionText(text)
}

function pickExplanationFromAnswerOptions(opts: unknown[]): string {
  for (const item of opts) {
    const o = item as Record<string, unknown>
    const isCorrect = Boolean(o.isCorrectAnswer ?? o.IsCorrectAnswer)
    if (!isCorrect) {
      continue
    }
    const text = pickOptionText(o.explanatoryText ?? o.ExplanatoryText)
    if (text.trim()) {
      return text.trim()
    }
  }
  for (const item of opts) {
    const o = item as Record<string, unknown>
    const text = pickOptionText(o.explanatoryText ?? o.ExplanatoryText)
    if (text.trim()) {
      return text.trim()
    }
  }
  return ''
}

function findHttpOrAbsolutePathInObject(
  obj: unknown,
  depth: number
): string | null {
  if (depth <= 0 || obj == null) {
    return null
  }
  if (typeof obj === 'string') {
    const t = obj.trim()
    if (!t) {
      return null
    }
    if (/^https?:\/\//i.test(t)) {
      return t
    }
    if (t.startsWith('//')) {
      return t
    }
    if (t.startsWith('/')) {
      return t
    }
    return null
  }
  if (typeof obj !== 'object') {
    return null
  }
  if (Array.isArray(obj)) {
    for (const x of obj) {
      const r = findHttpOrAbsolutePathInObject(x, depth - 1)
      if (r) {
        return r
      }
    }
    return null
  }
  for (const v of Object.values(obj)) {
    const r = findHttpOrAbsolutePathInObject(v, depth - 1)
    if (r) {
      return r
    }
  }
  return null
}

function imageUrlFromFileRecordFlat(f: Record<string, unknown>): string | null {
  const pathLikeKeys = new Set([
    'path',
    'Path',
    'relativeUrl',
    'RelativeUrl'
  ])
  const keys = [
    'url',
    'Url',
    'URL',
    'fileUrl',
    'FileUrl',
    'imageUrl',
    'ImageUrl',
    'downloadUrl',
    'DownloadUrl',
    'publicUrl',
    'PublicUrl',
    'previewUrl',
    'PreviewUrl',
    'thumbnailUrl',
    'ThumbnailUrl',
    'href',
    'Href',
    'link',
    'Link',
    'uri',
    'Uri',
    'src',
    'Src',
    'path',
    'Path',
    'relativeUrl',
    'RelativeUrl'
  ] as const
  for (const k of keys) {
    const v = f[k]
    if (typeof v !== 'string' || !v.trim()) {
      continue
    }
    const t = v.trim()
    if (pathLikeKeys.has(k)) {
      if (t.startsWith('/') || /^https?:\/\//i.test(t) || t.startsWith('//')) {
        return t
      }
      continue
    }
    return t
  }
  return null
}

function extractImageUrlFromApiFileRecord(f: Record<string, unknown>): string | null {
  const flat = imageUrlFromFileRecordFlat(f)
  if (flat) {
    return flat
  }
  const nestedKeys = [
    'file',
    'File',
    'attachment',
    'Attachment',
    'media',
    'Media',
    'image',
    'Image',
    'thumbnail',
    'Thumbnail'
  ]
  for (const nk of nestedKeys) {
    const v = f[nk]
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const inner = extractImageUrlFromApiFileRecord(v as Record<string, unknown>)
      if (inner) {
        return inner
      }
    }
  }
  return findHttpOrAbsolutePathInObject(f, 5)
}

function firstImageUrlFromFiles(files: unknown): string | null {
  if (!Array.isArray(files) || files.length === 0) {
    return null
  }
  for (const item of files) {
    if (!item || typeof item !== 'object') {
      continue
    }
    const f = item as Record<string, unknown>
    if (f.isDeleted === true || f.IsDeleted === true) {
      continue
    }
    const url = extractImageUrlFromApiFileRecord(f)
    if (url) {
      return url
    }
  }
  return null
}

function imageUrlFromSideImageFields(rec: Record<string, unknown>): string | null {
  const single = rec.image ?? rec.Image
  if (single && typeof single === 'object' && !Array.isArray(single)) {
    const u = extractImageUrlFromApiFileRecord(single as Record<string, unknown>)
    if (u) {
      return u
    }
  }
  return firstImageUrlFromFiles(rec.images ?? rec.Images)
}

function normalizeApiImageUrlForPreview(raw: string | null): string | null {
  if (!raw || !raw.trim()) {
    return null
  }
  const resolved = resolveFilesBurgdevUrl(raw)
  return resolved || null
}

function fileIdFromRecord(rec: Record<string, unknown>): string | null {
  const keys = ['fileId', 'FileId', 'fileContainerId', 'FileContainerId'] as const
  for (const k of keys) {
    const v = rec[k]
    if (typeof v === 'string' && v.trim()) {
      return v.trim()
    }
  }
  const files = rec.files ?? rec.Files
  if (!Array.isArray(files) || files.length === 0) {
    return null
  }
  const f0 = files[0]
  if (!f0 || typeof f0 !== 'object') {
    return null
  }
  const fr = f0 as Record<string, unknown>
  for (const k of keys) {
    const v = fr[k]
    if (typeof v === 'string' && v.trim()) {
      return v.trim()
    }
  }
  return null
}

export function mapApiQuestionsToReportDrafts(
  questions: unknown
): QuestionDraft[] {
  if (!Array.isArray(questions)) {
    return []
  }
  return questions.map((raw) => {
    const q = raw as Record<string, unknown>
    const answerOptionsRaw = q.answerOptions ?? q.AnswerOptions
    const opts = Array.isArray(answerOptionsRaw) ? answerOptionsRaw : []
    const options = opts.map((item) => {
      const o = item as Record<string, unknown>
      const oFiles = o.files ?? o.Files
      return {
        id: String(o.id ?? o.Id ?? ''),
        text: pickOptionText(o.text ?? o.Text),
        correct: Boolean(o.isCorrectAnswer ?? o.IsCorrectAnswer),
        imageFile: null as File | null,
        imageUrl: normalizeApiImageUrlForPreview(
          firstImageUrlFromFiles(oFiles) ?? imageUrlFromSideImageFields(o)
        ),
        optionFileContainerId: fileIdFromRecord(o)
      }
    })
    const qFiles = q.files ?? q.Files
    const kind = surveyKindFromQuestionType(q.type ?? q.Type)
    const firstExpl = opts.length
      ? pickOptionText(
          (opts[0] as Record<string, unknown>).explanatoryText ??
            (opts[0] as Record<string, unknown>).ExplanatoryText
        )
      : ''
    const lastExpl =
      opts.length > 1
        ? pickOptionText(
            (opts[opts.length - 1] as Record<string, unknown>).explanatoryText ??
              (opts[opts.length - 1] as Record<string, unknown>).ExplanatoryText
          )
        : ''
    const meta = decodeSurveyMeta(firstExpl)
    const needsOptions =
      kind === 'single' || kind === 'multiple' || kind === 'dropdown'
    const emptyOptionsFallback = needsOptions
      ? [
          {
            id: `opt-${String(q.id ?? q.Id ?? '')}-a`,
            text: '',
            correct: false,
            imageFile: null as File | null,
            imageUrl: null as string | null,
            optionFileContainerId: null as string | null
          },
          {
            id: `opt-${String(q.id ?? q.Id ?? '')}-b`,
            text: '',
            correct: false,
            imageFile: null,
            imageUrl: null,
            optionFileContainerId: null
          }
        ]
      : []

    let scaleMin: number | undefined
    let scaleMax: number | undefined
    let scaleMinLabel = meta?.scaleMinLabel ?? ''
    let scaleMaxLabel = ''
    let matrixRows: string[] | undefined
    let matrixCols: string[] | undefined
    const scaleType = pickScaleTypeFromQuestion(q)

    if (kind === 'scale') {
      scaleMin = 1
      if (scaleType != null && scaleType !== 0) {
        scaleMax = scaleMaxFromScaleType(scaleType)
      } else if (options.length > 1) {
        const nums = options
          .map((o) => parseInt(o.text.trim(), 10))
          .filter((n) => Number.isFinite(n))
        if (nums.length > 0) {
          scaleMin = Math.min(...nums)
          scaleMax = Math.max(...nums)
        }
      } else {
        scaleMax = 5
      }
      if (!scaleMinLabel && firstExpl && !decodeSurveyMeta(firstExpl)) {
        scaleMinLabel = firstExpl
      }
      if (lastExpl && !decodeSurveyMeta(lastExpl)) {
        scaleMaxLabel = lastExpl
      } else if (meta?.scaleMaxLabel) {
        scaleMaxLabel = meta.scaleMaxLabel
      }
    }

    if (kind === 'matrix') {
      matrixRows = options.map((o) => o.text)
      if (scaleType != null && scaleType !== 0) {
        matrixCols = buildScaleColsFromScaleType(scaleType)
      } else if (meta?.matrixCols && meta.matrixCols.length > 0) {
        matrixCols = meta.matrixCols
      } else {
        matrixCols = ['1', '2', '3', '4', '5']
      }
      scaleMin = 1
      scaleMax = matrixCols.length >= 10 ? 10 : 5
    }

    const explanation =
      kind === 'single' || kind === 'multiple'
        ? pickExplanationFromAnswerOptions(opts)
        : ''

    return {
      id: String(q.id ?? q.Id ?? ''),
      text: pickQuestionText(q.text ?? q.Text),
      imageFile: null,
      imageUrl: normalizeApiImageUrlForPreview(
        firstImageUrlFromFiles(qFiles) ?? imageUrlFromSideImageFields(q)
      ),
      questionFileContainerId: fileIdFromRecord(q),
      answerMode: answerModeFromQuestionType(q.type ?? q.Type),
      kind,
      isRequired: meta?.isRequired ?? kind !== 'info',
      scaleMin: scaleMin ?? 1,
      scaleMax: scaleMax ?? 5,
      scaleMinLabel,
      scaleMaxLabel,
      matrixRows,
      matrixCols,
      options: options.length > 0 ? options : emptyOptionsFallback,
      explanation,
      saved: true,
      serverQuestionId: String(q.id ?? q.Id ?? '')
    }
  })
}

export type TestReportDisplayStatus = 'draft' | 'published' | 'archived'

function strField(data: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = data[k]
    if (typeof v === 'string' && v.trim()) {
      return v.trim()
    }
  }
  return ''
}

export function mapTestItemStatusToReport(raw: unknown): TestReportDisplayStatus {
  if (raw === 'published' || raw === 'draft' || raw === 'archived') {
    return raw
  }
  const n = Number(raw)
  if (n === 1) {
    return 'published'
  }
  if (n === 2) {
    return 'archived'
  }
  return 'draft'
}

export function pickTestReportMeta(data: Record<string, unknown>): {
  authorName: string
  createdAtIso: string | null
  status: TestReportDisplayStatus
} {
  const authorName = strField(data, [
    'authorName',
    'AuthorName',
    'author',
    'Author',
    'createdByName',
    'CreatedByName',
    'userName',
    'UserName'
  ])
  const createdRaw =
    data.createdAt ??
    data.CreatedAt ??
    data.createDate ??
    data.CreateDate ??
    data.creationDate ??
    data.CreationDate
  let createdAtIso: string | null = null
  if (typeof createdRaw === 'string' && createdRaw.trim()) {
    const d = new Date(createdRaw)
    if (!Number.isNaN(d.getTime())) {
      createdAtIso = d.toISOString()
    }
  } else if (typeof createdRaw === 'number' && Number.isFinite(createdRaw)) {
    const d = new Date(createdRaw)
    if (!Number.isNaN(d.getTime())) {
      createdAtIso = d.toISOString()
    }
  }
  const status = mapTestItemStatusToReport(
    data.status ?? data.Status ?? data.testStatus ?? data.TestState
  )
  return { authorName, createdAtIso, status }
}
import type { QuestionDraft } from './questionTypes'
import { answerModeFromQuestionType, surveyKindFromQuestionType } from './questionTypes'
import {
  buildScaleColsFromScaleType,
  decodeSurveyMeta,
  pickScaleTypeFromQuestion,
  scaleMaxFromScaleType
} from 'shared/api/managerQuestionnaireQuestion'
import { resolveFilesBurgdevUrl } from 'shared/utils/resolveFilesBurgdevUrl'

export type ExplainModeHydrated =
  | 'never'
  | 'after_submit'
  | 'if_passed'
  | 'after_each'

type LangCode = 'ru' | 'en'

type Bilingual = Record<LangCode, string>

type LocalizedName = { lang: string; name: string }

function langKey(p: Record<string, unknown>): string {
  const raw =
    p.lang ?? p.Lang ?? p.language ?? p.Language ?? p.locale ?? p.Locale
  return typeof raw === 'string' ? raw.trim().toLowerCase() : ''
}

function textFromPart(p: Record<string, unknown>): string {
  const v =
    p.name ?? p.Name ?? p.value ?? p.Value ?? p.text ?? p.Text ?? p.title ?? p.Title
  return typeof v === 'string' ? v : ''
}

export function pickLocalizedText(parts: unknown, preferredLang: string): string {
  if (!Array.isArray(parts) || parts.length === 0) {
    return ''
  }
  const arr = parts as LocalizedName[]
  const lang = preferredLang.trim().toLowerCase().slice(0, 2)
  const rows = arr.map((p) =>
    p && typeof p === 'object' ? (p as unknown as Record<string, unknown>) : {}
  )
  const pref = rows.find((p) => langKey(p) === lang && textFromPart(p))
  const prefText = pref ? textFromPart(pref) : ''
  if (prefText) {
    return prefText
  }
  return pickName(arr, 'ru')
}

function pickName(parts: LocalizedName[] | undefined, lang: LangCode): string {
  if (!Array.isArray(parts) || parts.length === 0) {
    return ''
  }
  const rows = parts.map((p) =>
    p && typeof p === 'object' ? (p as unknown as Record<string, unknown>) : {}
  )
  const pref = rows.find((p) => langKey(p) === lang && textFromPart(p))
  const prefText = pref ? textFromPart(pref) : ''
  if (prefText) {
    return prefText
  }
  const ru = rows.find((p) => langKey(p) === 'ru')
  const ruText = ru ? textFromPart(ru) : ''
  if (ruText) {
    return ruText
  }
  const en = rows.find((p) => langKey(p) === 'en')
  const enText = en ? textFromPart(en) : ''
  if (enText) {
    return enText
  }
  const first = rows.find((p) => textFromPart(p))
  return first ? textFromPart(first) : ''
}

export function mapLocalizedToBilingual(parts: unknown): Bilingual {
  if (!Array.isArray(parts)) {
    return { ru: '', en: '' }
  }
  const arr = parts as LocalizedName[]
  return {
    ru: pickName(arr, 'ru'),
    en: pickName(arr, 'en')
  }
}

export function timeToCompleteToMinutes(timeStr: unknown): string {
  const s = String(timeStr ?? '').trim()
  if (!s) {
    return '0'
  }
  let days = 0
  let timePart = s
  const dotIdx = s.indexOf('.')
  if (dotIdx !== -1) {
    const d = parseInt(s.slice(0, dotIdx), 10)
    if (Number.isNaN(d)) {
      return '0'
    }
    days = d
    timePart = s.slice(dotIdx + 1)
  }
  const parts = timePart.split(':').map((x) => parseInt(x, 10))
  if (parts.some((n) => Number.isNaN(n))) {
    return '0'
  }
  const h = parts[0] ?? 0
  const m = parts[1] ?? 0
  const sec = parts[2] ?? 0
  return String(days * 24 * 60 + h * 60 + m + Math.floor(sec / 60))
}

export function mapExplainTypeToMode(n: unknown): ExplainModeHydrated {
  const v = Number(n)
  switch (v) {
    case 0:
      return 'never'
    case 1:
      return 'after_submit'
    case 2:
      return 'if_passed'
    case 3:
      return 'after_each'
    default:
      return 'after_submit'
  }
}

function pickOptionText(text: unknown): string {
  if (typeof text === 'string') {
    return text
  }
  if (text !== null && typeof text === 'object' && !Array.isArray(text)) {
    return textFromPart(text as Record<string, unknown>)
  }
  if (!Array.isArray(text)) {
    return ''
  }
  const arr = text as LocalizedName[]
  return pickName(arr, 'ru') || pickName(arr, 'en') || ''
}

function pickQuestionText(text: unknown): string {
  return pickOptionText(text)
}

function pickExplanationFromAnswerOptions(opts: unknown[]): string {
  for (const item of opts) {
    const o = item as Record<string, unknown>
    const isCorrect = Boolean(o.isCorrectAnswer ?? o.IsCorrectAnswer)
    if (!isCorrect) {
      continue
    }
    const text = pickOptionText(o.explanatoryText ?? o.ExplanatoryText)
    if (text.trim()) {
      return text.trim()
    }
  }
  for (const item of opts) {
    const o = item as Record<string, unknown>
    const text = pickOptionText(o.explanatoryText ?? o.ExplanatoryText)
    if (text.trim()) {
      return text.trim()
    }
  }
  return ''
}

function findHttpOrAbsolutePathInObject(
  obj: unknown,
  depth: number
): string | null {
  if (depth <= 0 || obj == null) {
    return null
  }
  if (typeof obj === 'string') {
    const t = obj.trim()
    if (!t) {
      return null
    }
    if (/^https?:\/\//i.test(t)) {
      return t
    }
    if (t.startsWith('//')) {
      return t
    }
    if (t.startsWith('/')) {
      return t
    }
    return null
  }
  if (typeof obj !== 'object') {
    return null
  }
  if (Array.isArray(obj)) {
    for (const x of obj) {
      const r = findHttpOrAbsolutePathInObject(x, depth - 1)
      if (r) {
        return r
      }
    }
    return null
  }
  for (const v of Object.values(obj)) {
    const r = findHttpOrAbsolutePathInObject(v, depth - 1)
    if (r) {
      return r
    }
  }
  return null
}

function imageUrlFromFileRecordFlat(f: Record<string, unknown>): string | null {
  const pathLikeKeys = new Set([
    'path',
    'Path',
    'relativeUrl',
    'RelativeUrl'
  ])
  const keys = [
    'url',
    'Url',
    'URL',
    'fileUrl',
    'FileUrl',
    'imageUrl',
    'ImageUrl',
    'downloadUrl',
    'DownloadUrl',
    'publicUrl',
    'PublicUrl',
    'previewUrl',
    'PreviewUrl',
    'thumbnailUrl',
    'ThumbnailUrl',
    'href',
    'Href',
    'link',
    'Link',
    'uri',
    'Uri',
    'src',
    'Src',
    'path',
    'Path',
    'relativeUrl',
    'RelativeUrl'
  ] as const
  for (const k of keys) {
    const v = f[k]
    if (typeof v !== 'string' || !v.trim()) {
      continue
    }
    const t = v.trim()
    if (pathLikeKeys.has(k)) {
      if (t.startsWith('/') || /^https?:\/\//i.test(t) || t.startsWith('//')) {
        return t
      }
      continue
    }
    return t
  }
  return null
}

function extractImageUrlFromApiFileRecord(f: Record<string, unknown>): string | null {
  const flat = imageUrlFromFileRecordFlat(f)
  if (flat) {
    return flat
  }
  const nestedKeys = [
    'file',
    'File',
    'attachment',
    'Attachment',
    'media',
    'Media',
    'image',
    'Image',
    'thumbnail',
    'Thumbnail'
  ]
  for (const nk of nestedKeys) {
    const v = f[nk]
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const inner = extractImageUrlFromApiFileRecord(v as Record<string, unknown>)
      if (inner) {
        return inner
      }
    }
  }
  return findHttpOrAbsolutePathInObject(f, 5)
}

function firstImageUrlFromFiles(files: unknown): string | null {
  if (!Array.isArray(files) || files.length === 0) {
    return null
  }
  for (const item of files) {
    if (!item || typeof item !== 'object') {
      continue
    }
    const f = item as Record<string, unknown>
    if (f.isDeleted === true || f.IsDeleted === true) {
      continue
    }
    const url = extractImageUrlFromApiFileRecord(f)
    if (url) {
      return url
    }
  }
  return null
}

function imageUrlFromSideImageFields(rec: Record<string, unknown>): string | null {
  const single = rec.image ?? rec.Image
  if (single && typeof single === 'object' && !Array.isArray(single)) {
    const u = extractImageUrlFromApiFileRecord(single as Record<string, unknown>)
    if (u) {
      return u
    }
  }
  return firstImageUrlFromFiles(rec.images ?? rec.Images)
}

function normalizeApiImageUrlForPreview(raw: string | null): string | null {
  if (!raw || !raw.trim()) {
    return null
  }
  const resolved = resolveFilesBurgdevUrl(raw)
  return resolved || null
}

function fileIdFromRecord(rec: Record<string, unknown>): string | null {
  const keys = ['fileId', 'FileId', 'fileContainerId', 'FileContainerId'] as const
  for (const k of keys) {
    const v = rec[k]
    if (typeof v === 'string' && v.trim()) {
      return v.trim()
    }
  }
  const files = rec.files ?? rec.Files
  if (!Array.isArray(files) || files.length === 0) {
    return null
  }
  const f0 = files[0]
  if (!f0 || typeof f0 !== 'object') {
    return null
  }
  const fr = f0 as Record<string, unknown>
  for (const k of keys) {
    const v = fr[k]
    if (typeof v === 'string' && v.trim()) {
      return v.trim()
    }
  }
  return null
}

export function mapApiQuestionsToReportDrafts(
  questions: unknown
): QuestionDraft[] {
  if (!Array.isArray(questions)) {
    return []
  }
  return questions.map((raw) => {
    const q = raw as Record<string, unknown>
    const answerOptionsRaw = q.answerOptions ?? q.AnswerOptions
    const opts = Array.isArray(answerOptionsRaw) ? answerOptionsRaw : []
    const options = opts.map((item) => {
      const o = item as Record<string, unknown>
      const oFiles = o.files ?? o.Files
      return {
        id: String(o.id ?? o.Id ?? ''),
        text: pickOptionText(o.text ?? o.Text),
        correct: Boolean(o.isCorrectAnswer ?? o.IsCorrectAnswer),
        imageFile: null as File | null,
        imageUrl: normalizeApiImageUrlForPreview(
          firstImageUrlFromFiles(oFiles) ?? imageUrlFromSideImageFields(o)
        ),
        optionFileContainerId: fileIdFromRecord(o)
      }
    })
    const qFiles = q.files ?? q.Files
    const kind = surveyKindFromQuestionType(q.type ?? q.Type)
    const firstExpl = opts.length
      ? pickOptionText(
          (opts[0] as Record<string, unknown>).explanatoryText ??
            (opts[0] as Record<string, unknown>).ExplanatoryText
        )
      : ''
    const lastExpl =
      opts.length > 1
        ? pickOptionText(
            (opts[opts.length - 1] as Record<string, unknown>).explanatoryText ??
              (opts[opts.length - 1] as Record<string, unknown>).ExplanatoryText
          )
        : ''
    const meta = decodeSurveyMeta(firstExpl)
    const needsOptions =
      kind === 'single' || kind === 'multiple' || kind === 'dropdown'
    const emptyOptionsFallback = needsOptions
      ? [
          {
            id: `opt-${String(q.id ?? q.Id ?? '')}-a`,
            text: '',
            correct: false,
            imageFile: null as File | null,
            imageUrl: null as string | null,
            optionFileContainerId: null as string | null
          },
          {
            id: `opt-${String(q.id ?? q.Id ?? '')}-b`,
            text: '',
            correct: false,
            imageFile: null,
            imageUrl: null,
            optionFileContainerId: null
          }
        ]
      : []

    let scaleMin: number | undefined
    let scaleMax: number | undefined
    let scaleMinLabel = meta?.scaleMinLabel ?? ''
    let scaleMaxLabel = ''
    let matrixRows: string[] | undefined
    let matrixCols: string[] | undefined
    const scaleType = pickScaleTypeFromQuestion(q)

    if (kind === 'scale') {
      scaleMin = 1
      if (scaleType != null && scaleType !== 0) {
        scaleMax = scaleMaxFromScaleType(scaleType)
      } else if (options.length > 1) {
        const nums = options
          .map((o) => parseInt(o.text.trim(), 10))
          .filter((n) => Number.isFinite(n))
        if (nums.length > 0) {
          scaleMin = Math.min(...nums)
          scaleMax = Math.max(...nums)
        }
      } else {
        scaleMax = 5
      }
      if (!scaleMinLabel && firstExpl && !decodeSurveyMeta(firstExpl)) {
        scaleMinLabel = firstExpl
      }
      if (lastExpl && !decodeSurveyMeta(lastExpl)) {
        scaleMaxLabel = lastExpl
      } else if (meta?.scaleMaxLabel) {
        scaleMaxLabel = meta.scaleMaxLabel
      }
    }

    if (kind === 'matrix') {
      matrixRows = options.map((o) => o.text)
      if (scaleType != null && scaleType !== 0) {
        matrixCols = buildScaleColsFromScaleType(scaleType)
      } else if (meta?.matrixCols && meta.matrixCols.length > 0) {
        matrixCols = meta.matrixCols
      } else {
        matrixCols = ['1', '2', '3', '4', '5']
      }
      scaleMin = 1
      scaleMax = matrixCols.length >= 10 ? 10 : 5
    }

    const explanation =
      kind === 'single' || kind === 'multiple'
        ? pickExplanationFromAnswerOptions(opts)
        : ''

    return {
      id: String(q.id ?? q.Id ?? ''),
      text: pickQuestionText(q.text ?? q.Text),
      imageFile: null,
      imageUrl: normalizeApiImageUrlForPreview(
        firstImageUrlFromFiles(qFiles) ?? imageUrlFromSideImageFields(q)
      ),
      questionFileContainerId: fileIdFromRecord(q),
      answerMode: answerModeFromQuestionType(q.type ?? q.Type),
      kind,
      isRequired: meta?.isRequired ?? kind !== 'info',
      scaleMin: scaleMin ?? 1,
      scaleMax: scaleMax ?? 5,
      scaleMinLabel,
      scaleMaxLabel,
      matrixRows,
      matrixCols,
      options: options.length > 0 ? options : emptyOptionsFallback,
      explanation,
      saved: true,
      serverQuestionId: String(q.id ?? q.Id ?? '')
    }
  })
}

export type TestReportDisplayStatus = 'draft' | 'published' | 'archived'

function strField(data: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = data[k]
    if (typeof v === 'string' && v.trim()) {
      return v.trim()
    }
  }
  return ''
}

export function mapTestItemStatusToReport(raw: unknown): TestReportDisplayStatus {
  if (raw === 'published' || raw === 'draft' || raw === 'archived') {
    return raw
  }
  const n = Number(raw)
  if (n === 1) {
    return 'published'
  }
  if (n === 2) {
    return 'archived'
  }
  return 'draft'
}

export function pickTestReportMeta(data: Record<string, unknown>): {
  authorName: string
  createdAtIso: string | null
  status: TestReportDisplayStatus
} {
  const authorName = strField(data, [
    'authorName',
    'AuthorName',
    'author',
    'Author',
    'createdByName',
    'CreatedByName',
    'userName',
    'UserName'
  ])
  const createdRaw =
    data.createdAt ??
    data.CreatedAt ??
    data.createDate ??
    data.CreateDate ??
    data.creationDate ??
    data.CreationDate
  let createdAtIso: string | null = null
  if (typeof createdRaw === 'string' && createdRaw.trim()) {
    const d = new Date(createdRaw)
    if (!Number.isNaN(d.getTime())) {
      createdAtIso = d.toISOString()
    }
  } else if (typeof createdRaw === 'number' && Number.isFinite(createdRaw)) {
    const d = new Date(createdRaw)
    if (!Number.isNaN(d.getTime())) {
      createdAtIso = d.toISOString()
    }
  }
  const status = mapTestItemStatusToReport(
    data.status ?? data.Status ?? data.testStatus ?? data.TestState
  )
  return { authorName, createdAtIso, status }
}
